import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaHeart, FaRegHeart, FaComment, FaShare, FaEllipsisH, FaTrash, FaEdit, FaPlay } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { postsAPI } from '../services/api';
import toast from 'react-hot-toast';
import Hls from 'hls.js';

const Card = styled.div`
  position: relative;
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
  aspect-ratio: 9/16;
  min-height: 400px;
  &:hover {
    transform: scale(1.02);
  }
`;
const Media = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;
const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 20px 12px 12px;
  z-index: 2;
`;
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  width: 100%;
`;
const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
`;
const UserName = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const TimeAgo = styled.div`
  color: #ccc;
  font-size: 11px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  margin-left: auto;
`;
const Content = styled.div`
  color: #fff;
  font-size: 13px;
  line-height: 1.3;
  margin-bottom: 12px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: #fff;
  font-size: 12px;
`;
const Action = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
`;
const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.2s ease;
  ${Card}:hover & {
    opacity: 1;
  }
`;
const Duration = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  z-index: 3;
`;

const PostCard = ({ post, onPostUpdate, onPostDelete }) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(post.is_liked || false);
  const [likeCount, setLikeCount] = useState(post.likes_count || 0);
  const [showOptions, setShowOptions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef(null);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleLike = async () => {
    try {
      if (isLiked) {
        await postsAPI.unlikePost(post.id);
        setLikeCount(prev => prev - 1);
        setIsLiked(false);
      } else {
        await postsAPI.likePost(post.id);
        setLikeCount(prev => prev + 1);
        setIsLiked(true);
      }
      const updatedPost = {
        ...post,
        is_liked: !isLiked,
        likes_count: isLiked ? likeCount - 1 : likeCount + 1
      };
      onPostUpdate && onPostUpdate(updatedPost);
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      setIsDeleting(true);
      try {
        await postsAPI.deletePost(post.id);
        onPostDelete && onPostDelete(post.id);
        toast.success('Post deleted successfully');
      } catch (error) {
        toast.error('Failed to delete post');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // TikTok style: only first media is shown as background
  const mainMedia = post.media && post.media.length > 0 ? post.media[0] : null;
  const isVideo = mainMedia && (mainMedia.type === 'video' || mainMedia.path?.includes('.mp4') || mainMedia.path?.includes('.mov') || mainMedia.path?.includes('.m3u8'));

  // Video hover play logic
  useEffect(() => {
    if (isVideo && hovered && videoRef.current) {
      if (mainMedia.path?.includes('.m3u8')) {
        if (Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(mainMedia.path);
          hls.attachMedia(videoRef.current);
          videoRef.current.hls = hls;
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            videoRef.current.play().catch(() => {});
          });
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          videoRef.current.src = mainMedia.path;
          videoRef.current.play().catch(() => {});
        }
      } else {
        videoRef.current.src = mainMedia.path;
        videoRef.current.play().catch(() => {});
      }
    } else if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      if (videoRef.current.hls) {
        videoRef.current.hls.destroy();
        videoRef.current.hls = null;
      }
    }
    // eslint-disable-next-line
  }, [hovered]);

  return (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Media Background */}
      {mainMedia && !isVideo && (
        <Media src={mainMedia.path} alt="Post media" />
      )}
      {mainMedia && isVideo && (
        <>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
            preload="metadata"
            poster={mainMedia.cover}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, borderRadius: 8 }}
          />
          {/* Play Button for Videos */}
          {!hovered && (
            <PlayButton>
              <FaPlay style={{ color: '#000', fontSize: '20px' }} />
            </PlayButton>
          )}
          {/* Duration Badge for Videos */}
          {mainMedia.duration && (
            <Duration>
              {formatDuration(mainMedia.duration)}
            </Duration>
          )}
        </>
      )}
      {/* Overlay with User Info and Actions */}
      <Overlay>
        <UserInfo>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Avatar
              src={post.user?.meta?.profile_image || '/default-avatar.png'}
              alt={post.user?.meta?.name || post.user?.username}
            />
            <UserName>@{post.user?.username || 'unknown'}</UserName>
          </div>
          {post.timeago && <TimeAgo>{post.timeago}</TimeAgo>}
        </UserInfo>
        {post.content && <Content>{post.content}</Content>}
        <Actions>
          <Action onClick={handleLike} title="Like">
            {isLiked ? <FaHeart style={{ color: '#ff4d4f' }} /> : <FaRegHeart />}
            {likeCount}
          </Action>
          <Action title="Comments">
            <FaComment />
            {post.comments_count || 0}
          </Action>
          <Action title="Share">
            <FaShare />
            Share
          </Action>
        </Actions>
      </Overlay>
    </Card>
  );
};

export default PostCard; 